// 📄 pages/Proyectos/components/CasoPruebaResultadoModal.tsx
// Se abre al mover un caso de prueba a "Aprobado" o "Fallido" — pide el
// detalle de la ejecución en vez de dejar que el estado cambie en seco.
import React, { useState } from 'react';
import { X, Save, RefreshCw } from 'lucide-react';
import {
  CasoPrueba, CasoPruebaTipoError, CASO_PRUEBA_TIPOS_ERROR,
  DetalleAprobacion, DetalleFallo
} from '../../../services/casoPruebaService.ts';

interface CasoPruebaResultadoModalProps {
  caso: CasoPrueba;
  estado: 'Aprobado' | 'Fallido';
  onClose: () => void;
  onConfirm: (detalle: DetalleAprobacion | DetalleFallo) => Promise<void>;
}

export default function CasoPruebaResultadoModal({ caso, estado, onClose, onConfirm }: CasoPruebaResultadoModalProps) {
  const [resultado, setResultado] = useState(caso.detalleAprobacion?.resultado || '');
  const [consideraciones, setConsideraciones] = useState(caso.detalleAprobacion?.consideraciones || '');
  const [tipoError, setTipoError] = useState<CasoPruebaTipoError>(caso.detalleFallo?.tipoError || 'Integración');
  const [descripcion, setDescripcion] = useState(caso.detalleFallo?.descripcion || '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const esAprobado = estado === 'Aprobado';
  const textoValido = esAprobado ? resultado.trim().length > 0 : descripcion.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    try {
      if (esAprobado) {
        await onConfirm({ resultado: resultado.trim(), consideraciones: consideraciones.trim() || undefined });
      } else {
        await onConfirm({ tipoError, descripcion: descripcion.trim() });
      }
    } catch (err: any) {
      setError(err.message || 'Error al guardar el resultado.');
      setGuardando(false);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-medium text-white">
            {esAprobado ? 'Aprobar caso de prueba' : 'Marcar caso de prueba como fallido'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto flex-1">
          <p className="text-xs text-gray-500">{caso.nombre}</p>

          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          {esAprobado ? (
            <>
              <div>
                <label className={labelClass}>Resultado del escenario*</label>
                <textarea
                  value={resultado} onChange={(e) => setResultado(e.target.value)} rows={4}
                  placeholder="Qué se probó y qué resultado dio..." className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Consideraciones</label>
                <textarea
                  value={consideraciones} onChange={(e) => setConsideraciones(e.target.value)} rows={3}
                  placeholder="Observaciones, casos límite, algo a tener en cuenta a futuro..." className={inputClass}
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className={labelClass}>Tipo de error*</label>
                <select value={tipoError} onChange={(e) => setTipoError(e.target.value as CasoPruebaTipoError)} className={inputClass}>
                  {CASO_PRUEBA_TIPOS_ERROR.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Qué falló*</label>
                <textarea
                  value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={4}
                  placeholder="Describí el error y en qué paso ocurrió..." className={inputClass}
                />
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">Cancelar</button>
            <button
              type="submit"
              disabled={guardando || !textoValido}
              className={`flex items-center gap-1.5 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all ${
                esAprobado ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-red-600 hover:bg-red-500'
              }`}
            >
              {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {esAprobado ? 'Aprobar' : 'Marcar como fallido'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
